import { Check, Search, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/cn";
import { InvitedBadge } from "@/shared/membership/invited-badge";

export interface MultiUserPickerOption {
  userId: string;
  displayName: string;
  email: string;
  avatarUrl?: string | null;
  /** True when this person is invited but has not accepted yet (pending member). */
  pending?: boolean;
}

interface Props {
  value: ReadonlyArray<string>;
  onChange: (next: ReadonlyArray<string>) => void;
  options: ReadonlyArray<MultiUserPickerOption>;
  /** Max picks. Selecting the (max + 1)th is a no-op. Defaults to 100. */
  max?: number;
  /** Trigger an initial focus on the search input when this changes (e.g.
   *  when the parent Popover/Dialog opens). */
  autoFocusKey?: string | number | boolean;
  /** Pixel cap on the scroll viewport. Pinned-header + pinned-footer
   *  containers in the parent need a hard height to clip overflow. */
  maxListHeight?: number;
  /** Search-input placeholder; defaults to "Search name or email…". */
  searchPlaceholder?: string;
  /** Empty-state for the case `options` is empty (no candidates at all). */
  emptyState?: ReactNode;
  /** Optional id for label association (`<Label htmlFor>`). Lands on the
   *  search input: it is the only focusable control a label can name here,
   *  and focusing it is what clicking the label should do. Mirrors
   *  `DatePicker`, and lets `<FormControl>` (a Slot) reach a real control. */
  id?: string;
}

/// Headless multi-select people picker. Renders the search-bar + scrollable
/// list + counter; the parent wraps it in a `<Popover>` (todo assignees
/// editor) or a `<Dialog>` (bulk reassign). Selection state is owned by
/// the parent, this list is presentational.
///
/// When the third consumer ships (likely @mentions autocomplete), refactor
/// the per-row checkbox into a render prop or slot. For now two consumers
/// are happy with the inline checkbox + avatar + name + email shape.
export function MultiUserPickerList({
  value,
  onChange,
  options,
  max = 100,
  autoFocusKey,
  maxListHeight = 240,
  searchPlaceholder = "Search name or email…",
  emptyState,
  id,
}: Props): ReactNode {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQ("");
    if (autoFocusKey !== undefined) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [autoFocusKey]);

  const candidates = useMemo(() => {
    const sorted = [...options].sort((a, b) => a.displayName.localeCompare(b.displayName));
    const needle = q.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter(
      (o) => o.displayName.toLowerCase().includes(needle) || o.email.toLowerCase().includes(needle),
    );
  }, [options, q]);

  function toggle(userId: string) {
    if (value.includes(userId)) {
      onChange(value.filter((id) => id !== userId));
    } else if (value.length < max) {
      onChange([...value, userId]);
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <Search aria-hidden="true" className="size-3.5 shrink-0 text-text-tertiary" />
        <Input
          id={id}
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-7 border-0 px-0 text-sm shadow-none focus-visible:ring-0"
        />
        {q ? (
          <button
            type="button"
            onClick={() => setQ("")}
            aria-label="Clear search"
            className="inline-flex size-5 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-subtle hover:text-text-primary"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>
      <div className="overflow-y-auto py-1" style={{ maxHeight: maxListHeight }}>
        {options.length === 0 ? (
          (emptyState ?? (
            <p className="px-3 py-6 text-center text-xs text-text-tertiary">Loading…</p>
          ))
        ) : candidates.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-text-tertiary">No matches</p>
        ) : (
          candidates.map((o) => {
            const checked = value.includes(o.userId);
            const disabled = !checked && value.length >= max;
            return (
              <button
                key={o.userId}
                type="button"
                onClick={() => toggle(o.userId)}
                disabled={disabled}
                className={cn(
                  "flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors",
                  "hover:bg-surface-subtle disabled:opacity-50",
                  checked && "bg-surface-subtle",
                )}
              >
                <span
                  className={cn(
                    "grid size-4 shrink-0 place-items-center rounded border",
                    checked
                      ? "border-text-brand bg-text-brand text-white"
                      : "border-border bg-card",
                  )}
                >
                  {checked ? <Check aria-hidden="true" className="size-3" /> : null}
                </span>
                <Avatar className="size-6 shrink-0">
                  <AvatarImage src={o.avatarUrl ?? undefined} alt="" />
                  <AvatarFallback className="bg-pulse-soft text-3xs text-text-brand-strong">
                    {initials(o.displayName)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-sm text-text-primary">{o.displayName}</span>
                    {o.pending ? <InvitedBadge /> : null}
                  </span>
                  <span className="truncate text-xs text-text-tertiary">{o.email}</span>
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]?.charAt(0).toUpperCase() ?? "?";
  return `${parts[0]?.charAt(0) ?? ""}${parts[parts.length - 1]?.charAt(0) ?? ""}`.toUpperCase();
}
